/**
 * Toasts — Figma `Overlay/Toast` (node 6:662), the component that carries **Undo**.
 *
 * Measured off the node: `brand/ink` fill, `radius` 10, h-44, px-14, 10px gap, an 18px
 * check glyph, `Body/13` in `text/inverse`, a 1×16 divider at `rgba(255,255,255,.24)`,
 * and the action in `Body/14-Med` `accent/sky`.
 *
 * THREE THINGS THE DESIGN DOES NOT STATE, RECORDED AS JUDGMENTS:
 *
 * 1. **Where it sits.** The node is authored on the Components page with no placement.
 *    Bottom-centre is used, stacked above `Table/BulkBar` (which is also bottom-centre)
 *    so the two never overlap — a toast that covers the bulk bar's "Clear" would trap a
 *    selection behind it.
 *
 * 2. **How long it lives.** Part A defines undo as "the client issuing the reverse stage
 *    PATCH within the toast window", so the window is a functional value, not a
 *    decoration. 8s: long enough to read four words and move a mouse, short enough that
 *    a stale Undo is not still sitting there after the next drag. Hovering or focusing
 *    the toast STOPS the clock — an Undo that vanishes from under the cursor is the one
 *    failure mode that makes the feature useless.
 *
 * 3. **What a failure looks like.** Figma authored exactly one toast, with a check. A
 *    refusal reuses the SAME shell with the glyph swapped and no action, rather than
 *    introducing a red toast: `status/danger/text` (#ca2525) on `brand/ink` measures
 *    2.0:1 and would be less legible than the white it replaced. Nothing new is
 *    invented, and the failure is also announced (`role="alert"`) instead of relying on
 *    colour that is not there.
 *
 * ===========================================================================
 * PHASE 2.5a · L6 + LEVER 7 — THE ENTRANCE, AND THE EDGE IT DID NOT HAVE
 * ===========================================================================
 * ENTRANCE. A toast is the one element in this app that genuinely appears from
 * nowhere, and it appeared instantly — 44px of ink materialising over the table,
 * which reads as a rendering glitch before it reads as a message. It now rises
 * `space/8` and fades in over `--crf-motion-settle` (150ms, the rule-4 ceiling
 * itself, because an entrance is the slowest thing the token set allows). The
 * keyframe is `crf-toast-in` in `app.css` and keys `opacity` and `transform` and
 * nothing else; `motion-safe:` is `@media (prefers-reduced-motion: no-preference)`
 * verbatim. Under `reduce` the toast simply appears — which is what it did
 * before, so reduced motion loses nothing.
 *
 * The animation is on the CARD, not on the stack that holds it: the stack is
 * `pointer-events-none` and spans the viewport, and animating it would have moved
 * every toast already on screen each time a new one arrived.
 *
 * THE DARK-MODE EDGE. `brand/ink` #1c1c1c is mode-invariant, and `bg/page` in dark
 * is #161616 — so the toast measured **1.06:1** against the page behind it. Its
 * one separating device, `0 8px 12px rgba(20,26,46,.24)`, is a dark shadow on a
 * dark page and does nothing there either. The text stayed legible (17.04:1 of
 * `text/inverse` on the fill), so this was never an AA failure — it was a black
 * rectangle with no boundary, floating on black.
 *
 * `dark:ring-1 dark:ring-line-strong` gives it one. `border/strong` #d4d9e2 is
 * mode-invariant, which is exactly why it works here: 12.77:1 against the page
 * and 12.02:1 against the toast's own fill. Light mode gets no ring — the ink
 * fill is already 17.04:1 against a white page and needs no help.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ToastTone = 'success' | 'error';

export interface ToastAction {
  label: string;
  /** Runs, then the toast closes. An Undo that stays up invites a double undo. */
  onAction: () => void;
}

export interface ToastSpec {
  text: string;
  tone?: ToastTone;
  action?: ToastAction;
}

interface ToastEntry extends ToastSpec {
  id: number;
}

export const TOAST_DURATION_MS = 8_000;

interface ToastApi {
  push: (spec: ToastSpec) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

let nextToastId = 1;

export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<readonly ToastEntry[]>([]);

  const dismiss = useCallback((id: number): void => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((spec: ToastSpec): void => {
    const entry: ToastEntry = { ...spec, id: nextToastId };
    nextToastId += 1;
    // Newest first, and at most three on screen. An unbounded stack after a bulk
    // operation would cover the table it is reporting on.
    setToasts((current) => [entry, ...current].slice(0, 3));
  }, []);

  const api = useMemo<ToastApi>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        // `pointer-events-none` on the stack, restored per toast: the container spans
        // the width of the screen and would otherwise swallow clicks on the table.
        className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast must be used inside <ToastProvider>.');
  return api;
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastEntry;
  onDismiss: (id: number) => void;
}): React.JSX.Element {
  const [paused, setPaused] = useState(false);

  // `onDismiss` is the provider's `useCallback` with no dependencies, so it is referentially
  // stable and can be a dependency directly — no ref, and therefore nothing read during
  // render.
  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(() => onDismiss(toast.id), TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [toast.id, paused, onDismiss]);

  const error = toast.tone === 'error';
  const Icon = error ? CircleAlert : CircleCheck;

  return (
    <div
      role={error ? 'alert' : 'status'}
      aria-live={error ? 'assertive' : 'polite'}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cn(
        'pointer-events-auto flex min-h-11 items-center gap-2.5 px-3.5 py-2',
        // `radius` 10 is the node's own raw value; the token set has 8 and 12 and no 10,
        // so this is the design's number rather than a substituted token.
        'rounded-[10px] bg-ink text-fg-inverse shadow-[0_8px_12px_0_rgba(20,26,46,0.24)]',
        // The boundary the shadow cannot draw on a #161616 page. See the header.
        'dark:ring-1 dark:ring-line-strong',
        // 150ms, transform + opacity, keyframe in `app.css`. See the header.
        'motion-safe:animate-toast-in',
      )}
    >
      <Icon aria-hidden="true" className="size-[18px] shrink-0" />
      <p className="text-body13">{toast.text}</p>
      {toast.action === undefined ? null : (
        <>
          <span aria-hidden="true" className="h-4 w-px shrink-0 bg-fg-inverse/25" />
          <button
            type="button"
            onClick={() => {
              toast.action?.onAction();
              onDismiss(toast.id);
            }}
            className="shrink-0 text-body font-medium text-sky hover:underline"
          >
            {toast.action.label}
          </button>
        </>
      )}
    </div>
  );
}
